/**
 * Pure aggregation helpers for the platform-signups dashboard.
 *
 * Ported verbatim from aieweb/lib/signups-aggregation.ts (PAR-6098) — the
 * date math, bucket-grid construction, and summary boundaries are identical so
 * the migrated report produces the same numbers. The only change vs the source
 * is the row type: here the helpers operate on the lightweight projection shape
 * `{ signupDate: string }` (the admin-dashboard consumes the server projection
 * endpoint, not the full org/orphan DTOs), so the helpers are typed against a
 * minimal `SignupDateRow` instead of importing the fat row interfaces.
 *
 * Every function takes an injected `now: Date` and `timezone` for testability —
 * no helper calls `new Date()` internally, so the same fixture can be tested
 * against multiple timezones and fixed instants.
 */

/** Minimal row shape the aggregation helpers read — only `signupDate` matters. */
export interface SignupDateRow {
  signupDate: string;
}

// ---------------------------------------------------------------------------
// Date math — all helpers operate in the caller-supplied IANA timezone.
// ---------------------------------------------------------------------------

/**
 * Format a Date as YYYY-MM-DD in the given IANA timezone. The output is
 * lexicographically comparable, so two dates compare equal as the same
 * calendar day in the target timezone iff their formatted strings match.
 */
export function dateKeyInTimezone(date: Date, timezone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/**
 * Return a Date pinned to 00:00:00 LOCAL (in `timezone`) of the same
 * calendar day as `now`.
 */
export function localDayStart(now: Date, timezone: string): Date {
  const dateKey = dateKeyInTimezone(now, timezone); // "YYYY-MM-DD"
  const naive = new Date(`${dateKey}T00:00:00Z`);
  const offsetMs = timezoneOffsetMs(naive, timezone);
  const firstPass = new Date(naive.getTime() - offsetMs);
  if (dateKeyInTimezone(firstPass, timezone) === dateKey) return firstPass;
  const correction = timezoneOffsetMs(firstPass, timezone);
  return new Date(naive.getTime() - correction);
}

/**
 * Return a Date pinned to Monday 00:00:00 LOCAL (ISO week start) of the
 * week containing `now` in the target timezone.
 */
export function localWeekStart(now: Date, timezone: string): Date {
  const dayStart = localDayStart(now, timezone);
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
  }).format(dayStart);
  const dayOffset: Record<string, number> = {
    Mon: 0,
    Tue: 1,
    Wed: 2,
    Thu: 3,
    Fri: 4,
    Sat: 5,
    Sun: 6,
  };
  const offsetDays = dayOffset[weekday] ?? 0;
  // Calendar-day subtraction (DST-safe) rather than naive `offsetDays * 24h` in
  // ms: the latter lands an hour off (or on the wrong day) if a DST transition
  // falls between the day and its Monday. US/EU transitions happen on the
  // week's own Sunday so this path is correct today, but the naive form is
  // fragile for non-Sunday-transition zones — fixed as a class (PR#11).
  return shiftLocalDays(dayStart, timezone, -offsetDays);
}

/**
 * Shift a local-midnight instant by a whole number of CALENDAR days in the
 * target timezone, returning the resulting local midnight. The day arithmetic
 * runs on the date string in UTC (immune to DST), then re-anchors to local
 * midnight via `parseLocalDate` — so a fall-back / spring-forward between the
 * two dates never leaves the result an hour off or on the wrong day. This is
 * the DST-safe replacement for `date.getTime() - N * 24h` on calendar
 * boundaries (PR#11 DST fix).
 */
function shiftLocalDays(localMidnight: Date, timezone: string, deltaDays: number): Date {
  const key = dateKeyInTimezone(localMidnight, timezone); // YYYY-MM-DD in tz
  const utc = new Date(`${key}T00:00:00Z`);
  utc.setUTCDate(utc.getUTCDate() + deltaDays);
  const shiftedKey = utc.toISOString().slice(0, 10);
  return parseLocalDate(shiftedKey, timezone);
}

/**
 * Compute the UTC offset (in ms) for a given UTC instant in the target
 * IANA timezone. Positive offsets are AHEAD of UTC.
 */
function timezoneOffsetMs(utcInstant: Date, timezone: string): number {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const parts = fmt.formatToParts(utcInstant);
  const lookup: Record<string, string> = {};
  for (const p of parts) lookup[p.type] = p.value;
  const wallClockUtc = Date.UTC(
    parseInt(lookup.year, 10),
    parseInt(lookup.month, 10) - 1,
    parseInt(lookup.day, 10),
    parseInt(lookup.hour, 10),
    parseInt(lookup.minute, 10),
    parseInt(lookup.second, 10)
  );
  return wallClockUtc - utcInstant.getTime();
}

// ---------------------------------------------------------------------------
// Summary helpers
// ---------------------------------------------------------------------------

export interface SignupsSummary {
  total: number;
  thisWeek: number;
  priorWeek: number;
  today: number;
  priorDay: number;
}

/**
 * Compute the summary stats from the full lifetime row sets. Always uses
 * lifetime totals — date-range filtering is for table + chart, NOT summary.
 *
 * Both `orgs` and `orphans` contribute to the totals. The admin-dashboard
 * passes (real + demo) rows as `orgs` and orphan rows as `orphans`, so the
 * "total" equals the sum of all three segment tabs (per outcomes).
 */
export function summarizeSignups(
  orgs: SignupDateRow[],
  orphans: SignupDateRow[],
  now: Date,
  timezone: string
): SignupsSummary {
  const allDates: Date[] = [];
  for (const r of orgs) {
    const d = parseIsoSafe(r.signupDate);
    if (d) allDates.push(d);
  }
  for (const r of orphans) {
    const d = parseIsoSafe(r.signupDate);
    if (d) allDates.push(d);
  }

  const dayStart = localDayStart(now, timezone);
  const weekStart = localWeekStart(now, timezone);
  // Prior-period boundaries via calendar-day subtraction (DST-safe). Naive
  // `-24h` / `-7*24h` ms lands an hour off — or on the wrong day — when the
  // prior day or prior week spans a DST transition (e.g. the Monday after the
  // Nov 1 2026 fall-back in America/New_York), mis-bucketing boundary signups
  // in the KPI comparisons (PR#11 finding).
  const priorDayStart = shiftLocalDays(dayStart, timezone, -1);
  const priorWeekStart = shiftLocalDays(weekStart, timezone, -7);

  let today = 0;
  let priorDay = 0;
  let thisWeek = 0;
  let priorWeek = 0;

  for (const d of allDates) {
    const t = d.getTime();
    if (t >= dayStart.getTime() && t <= now.getTime()) today++;
    if (t >= priorDayStart.getTime() && t < dayStart.getTime()) priorDay++;
    if (t >= weekStart.getTime() && t <= now.getTime()) thisWeek++;
    if (t >= priorWeekStart.getTime() && t < weekStart.getTime()) priorWeek++;
  }

  return {
    total: allDates.length,
    today,
    priorDay,
    thisWeek,
    priorWeek,
  };
}

// ---------------------------------------------------------------------------
// Date-range filter
// ---------------------------------------------------------------------------

export type DateRangePreset = "all" | "7d" | "30d" | "90d" | "custom";

export interface DateRange {
  preset: DateRangePreset;
  /** Required for "custom"; ignored otherwise. */
  customStart?: string; // YYYY-MM-DD
  /** Required for "custom"; interpreted as INCLUSIVE end-of-day in caller's tz. */
  customEnd?: string; // YYYY-MM-DD
}

export interface ResolvedRange {
  start: Date;
  end: Date; // exclusive — half-open `[start, end)`
}

/**
 * Resolve a `DateRange` against `now` + `timezone` into a half-open interval
 * `[start, end)`. Returns `null` for "all" (caller skips filtering).
 *
 * Custom-range `customEnd` is INCLUSIVE — the half-open interval extends to
 * the start of the day AFTER `customEnd`. `customStart` > `customEnd` returns
 * `null`; caller renders the inline validation error.
 */
export function resolveDateRange(
  range: DateRange,
  now: Date,
  timezone: string
): ResolvedRange | null {
  if (range.preset === "all") return null;

  if (range.preset === "custom") {
    if (!range.customStart || !range.customEnd) return null;
    if (range.customStart > range.customEnd) return null; // invalid

    const start = parseLocalDate(range.customStart, timezone);
    const endInclusive = parseLocalDate(range.customEnd, timezone);
    // Half-open: `end` is local midnight of the day AFTER `customEnd`. This is a
    // CALENDAR boundary, so use the DST-safe calendar-day shift rather than
    // `+24h` in ms — the latter lands an hour off when `customEnd` is a DST
    // transition day (e.g. the Nov 1 2026 fall-back in America/New_York),
    // excluding the last hour of the end date (PR#11 minor).
    const end = shiftLocalDays(endInclusive, timezone, 1);
    return { start, end };
  }

  const days = range.preset === "7d" ? 7 : range.preset === "30d" ? 30 : 90;
  const end = now;
  // Intentional: preset chips are SLIDING wall-clock windows measured back from
  // `now` (a "last 7 days" = last 168 hours span), NOT calendar-day boundaries,
  // so `start` need not be a local midnight and the naive ms subtraction is
  // correct here. Do NOT "fix" this with shiftLocalDays — that would snap the
  // window to calendar days and change the product semantics (PR#11 nit).
  const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return { start, end };
}

function parseLocalDate(yyyyMmDd: string, timezone: string): Date {
  const naive = new Date(`${yyyyMmDd}T00:00:00Z`);
  const offsetMs = timezoneOffsetMs(naive, timezone);
  return new Date(naive.getTime() - offsetMs);
}

/**
 * Resolve a `DateRange` into the wire params the paged endpoint expects.
 *
 * The contract differs by preset kind to avoid a stale-anchor bug (PAR-7354
 * review finding [1]):
 * - Preset windows (7d/30d/90d) send ONLY `rangeStart`. The snapshot moment is
 *   the natural, always-current upper bound, so omitting `rangeEnd` stops a
 *   `rangeEnd` pinned to a previous snapshot's `generatedAt` from silently
 *   excluding the newest signups after an idle + Refresh.
 * - Custom sends an explicit half-open `[start, end)`.
 * - All-time / invalid / incomplete-custom send neither (unfiltered).
 */
export function resolveRangeParams(
  range: DateRange,
  now: Date,
  timezone: string
): { rangeStart?: string; rangeEnd?: string } {
  const resolved = resolveDateRange(range, now, timezone);
  if (!resolved) return {};
  if (range.preset === "custom") {
    return {
      rangeStart: resolved.start.toISOString(),
      rangeEnd: resolved.end.toISOString(),
    };
  }
  // Preset window: lower bound only.
  return { rangeStart: resolved.start.toISOString() };
}

/**
 * True for the fixed-lookback presets whose `rangeStart` is measured back from
 * `now`, and which therefore must be re-resolved (and the table refetched) when
 * a newer snapshot anchor is adopted. All-time (no bounds) and Custom (fixed
 * calendar dates, independent of `now`) do not need re-resolution.
 */
export function isPresetRange(range: DateRange): boolean {
  return range.preset === "7d" || range.preset === "30d" || range.preset === "90d";
}

/**
 * True only when a Custom range has BOTH dates filled and the end precedes the
 * start (PAR-7354 review finding [3+5]). This is the single trigger that LOCKS
 * the view: while it holds, no table-mutating interaction commits and no request
 * is issued, so the table can never silently fall back to all-time data. A
 * one-date-filled custom range is NOT invalid — it is simply unfiltered until
 * both dates are chosen.
 */
export function isCustomRangeInvalid(range: DateRange): boolean {
  return (
    range.preset === "custom" &&
    !!range.customStart &&
    !!range.customEnd &&
    range.customStart > range.customEnd
  );
}

/** Apply the resolved range as a row-level predicate. Returns rows whose
 *  `signupDate` falls in `[start, end)`. */
export function applyDateRange<T extends SignupDateRow>(
  rows: T[],
  range: ResolvedRange | null
): T[] {
  if (!range) return rows;
  const startMs = range.start.getTime();
  const endMs = range.end.getTime();
  return rows.filter((r) => {
    const d = parseIsoSafe(r.signupDate);
    if (!d) return false;
    const t = d.getTime();
    return t >= startMs && t < endMs;
  });
}

function parseIsoSafe(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d : null;
}

// ---------------------------------------------------------------------------
// Bucket helpers — group signups into day / week / month buckets for the
// time-series chart. Empty buckets are emitted (zero count) so the chart
// renders as a contiguous timeline.
// ---------------------------------------------------------------------------

export type BucketSize = "day" | "week" | "month";

export interface SignupBucket {
  bucketStart: Date;
  /** YYYY-MM-DD for daily, "Week of YYYY-MM-DD" for weekly, YYYY-MM monthly. */
  label: string;
  count: number;
}

/**
 * Auto-pick a bucket size based on the range's calendar span.
 * - ≤ 60 days → day
 * - ≤ 548 days (≈18 months) → week
 * - otherwise → month
 */
export function autoPickBucket(start: Date, end: Date): BucketSize {
  const days = Math.ceil((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
  if (days <= 60) return "day";
  if (days <= 548) return "week";
  return "month";
}

/**
 * True iff the bucket size is valid for the range. `month` is disabled at
 * ≤14 days (one bar = useless); `week`/`day` are always valid.
 */
export function isBucketValid(bucket: BucketSize, start: Date, end: Date): boolean {
  if (bucket !== "month") return true;
  const days = Math.ceil((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
  return days > 14;
}

/**
 * Bucket a row set into time-series points. Combines `orgs` + `orphans`
 * unconditionally (single combined series). Empty buckets emitted for chart
 * contiguity. The admin-dashboard passes real-segment rows as `orgs` and
 * orphan-segment rows as `orphans` (demo excluded) so the chart covers real +
 * orphan only.
 */
export function bucketSignups(
  orgs: SignupDateRow[],
  orphans: SignupDateRow[],
  start: Date,
  end: Date,
  bucket: BucketSize,
  timezone: string
): SignupBucket[] {
  const grid = buildBucketGrid(start, end, bucket, timezone);
  const gridStartsMs = grid.map((g) => g.bucketStart.getTime());

  for (const r of orgs) {
    incrementBucketForRow(r.signupDate, start, end, grid, gridStartsMs);
  }
  for (const r of orphans) {
    incrementBucketForRow(r.signupDate, start, end, grid, gridStartsMs);
  }

  return grid;
}

function incrementBucketForRow(
  iso: string,
  start: Date,
  end: Date,
  grid: SignupBucket[],
  gridStartsMs: number[]
): void {
  const date = parseIsoSafe(iso);
  if (!date) return;
  const t = date.getTime();
  if (t < start.getTime() || t >= end.getTime()) return;
  // Floor: largest gridStart <= t. Binary search.
  let lo = 0;
  let hi = gridStartsMs.length - 1;
  let idx = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    if (gridStartsMs[mid] <= t) {
      idx = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  if (idx >= 0) grid[idx].count++;
}

function buildBucketGrid(
  start: Date,
  end: Date,
  bucket: BucketSize,
  timezone: string
): SignupBucket[] {
  const grid: SignupBucket[] = [];
  let cursor = bucketStartFor(start, bucket, timezone);
  while (cursor.getTime() < end.getTime()) {
    grid.push({
      bucketStart: new Date(cursor),
      label: bucketLabel(cursor, bucket, timezone),
      count: 0,
    });
    cursor = advanceBucket(cursor, bucket, timezone);
  }
  return grid;
}

function bucketStartFor(date: Date, bucket: BucketSize, timezone: string): Date {
  if (bucket === "day") return localDayStart(date, timezone);
  if (bucket === "week") return localWeekStart(date, timezone);
  const dateKey = dateKeyInTimezone(date, timezone); // YYYY-MM-DD
  const firstOfMonth = `${dateKey.slice(0, 7)}-01`; // YYYY-MM-01
  return parseLocalDate(firstOfMonth, timezone);
}

function advanceBucket(cursor: Date, bucket: BucketSize, timezone: string): Date {
  if (bucket === "day") {
    const naiveNext = new Date(cursor.getTime() + 24 * 60 * 60 * 1000);
    return localDayStart(naiveNext, timezone);
  }
  if (bucket === "week") {
    const naiveNext = new Date(cursor.getTime() + 7 * 24 * 60 * 60 * 1000);
    return localWeekStart(naiveNext, timezone);
  }
  const dateKey = dateKeyInTimezone(cursor, timezone); // YYYY-MM-DD
  const [yearStr, monthStr] = dateKey.split("-");
  let year = parseInt(yearStr, 10);
  let month = parseInt(monthStr, 10);
  month++;
  if (month > 12) {
    month = 1;
    year++;
  }
  const nextFirst = `${year}-${String(month).padStart(2, "0")}-01`;
  return parseLocalDate(nextFirst, timezone);
}

function bucketLabel(cursor: Date, bucket: BucketSize, timezone: string): string {
  if (bucket === "day") return dateKeyInTimezone(cursor, timezone);
  if (bucket === "week") return `Week of ${dateKeyInTimezone(cursor, timezone)}`;
  return dateKeyInTimezone(cursor, timezone).slice(0, 7); // YYYY-MM
}

/**
 * "All time" range derivation: span from the earliest signupDate to `now`.
 * Used by the chart when `resolveDateRange` returns null on `preset: "all"` —
 * the chart still needs a window so it can render something.
 */
export function deriveAllTimeRange(
  rows: SignupDateRow[],
  now: Date
): ResolvedRange | null {
  let earliest: number | null = null;
  for (const r of rows) {
    const t = Date.parse(r.signupDate);
    if (Number.isFinite(t) && (earliest === null || t < earliest)) earliest = t;
  }
  if (earliest === null) return null;
  return { start: new Date(earliest), end: now };
}
